// Contexto de autenticação: expõe o usuário logado (via cookie de sessão) e as
// ações de login/logout. Nunca chama `fetch` diretamente — usa o cliente único
// `ui/src/lib/api.ts`, conforme o princípio de fronteiras do frontend.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { ApiError, HTTP_STATUS_UNAUTHORIZED, fetchMe, login as apiLogin, logout as apiLogout } from "../lib/api";
import type { AuthUser } from "../types";

const GENERIC_FETCH_ME_ERROR = "Não foi possível verificar sua sessão.";

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetchMe()
      .then((fetchedUser) => {
        if (cancelled) return;
        setUser(fetchedUser);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setUser(null);
        if (err instanceof ApiError && err.status === HTTP_STATUS_UNAUTHORIZED) {
          setError(null);
        } else {
          setError(GENERIC_FETCH_ME_ERROR);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const loggedInUser = await apiLogin(email, password);
    setUser(loggedInUser);
    setError(null);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout().catch(() => undefined);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
}
